<template>
  <div class="artist-section relative">
    <VPLabel label="Artists" />
    <template v-if="newArtistsEnabled">
      <VueDraggable
        :model-value="allArtists"
        :animation="150"
        :disabled="artistsReordering"
        ghost-class="artist-ghost"
        handle=".artist-handle"
        filter=".artists-loading-spinner"
        class="relative"
        @update:model-value="onArtistsReorder"
      >
        <div
          v-for="eventArtist in allArtists"
          :key="eventArtist.id"
          class="flex justify-start gap-2 items-center"
        >
          <div class="artist-handle">{{ eventArtist.artist?.name }}</div>

          <VPButton
            class="vp-btn vp-btn-xs vp-btn-primary vp-btn-outline px-2"
            :label="$gettext('Edit')"
            @click="emit('edit', eventArtist.artist?.id)"
          />

          <VPButton
            class="vp-btn vp-btn-xs vp-btn-primary vp-btn-outline px-2"
            :label="$gettext('Remove')"
            @click="onEventArtistRemove(eventArtist)"
          />
        </div>

        <Transition name="fade">
          <div
            v-if="artistsReordering"
            class="artists-loading-spinner"
          >
            <div class="loader-backdrop" />
            <VPLoadingSpinner loading />
          </div>
        </Transition>
      </VueDraggable>

      <ArtistsDropdown
        :label="$gettext('Add artist')"
        @add="onArtistAdd"
        @select="onArtistSelect"
      />
    </template>

    <template v-else>
      <template
        v-for="artist in artists"
        :key="artist.listId"
      >
        <ArtistRow
          :form-id="artist.formId"
          :artist="artist.artist"
          :list-id="artist.listId"
          @remove="remove"
        />
      </template>

      <VPButton
        class="vp-btn vp-btn-primary vp-btn-solid vp-btn-md mt-3 pl-4 pr-4"
        size="md"
        height="2rem"
        @click.prevent="add"
      >
        {{ $gettext('Add artist') }}
      </VPButton>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, ref, watch } from 'vue';
import VPLabel from '@/components/form/VPLabel.vue';
import ArtistRow from '@/components/announce-modal/ArtistRow.vue';
import type { AnnounceArtist } from '@/generated/graphql';
import { useAccountStore } from '@/stores/account';
import { API } from '@vp/common';
import { VPButton, VPLoadingSpinner } from '@vp/vue-components';
import { useGettext } from 'vue3-gettext';
import ArtistsDropdown from '@/components/artist/ArtistsDropdown.vue';
import { useEventArtistStore } from '@/stores/eventArtists';
import { useToast } from 'vue-toastification';
import * as Sentry from '@sentry/vue';
import { VueDraggable } from 'vue-draggable-plus';
import { LoadingState } from '@/util/LoadingState';

type EventArtist = API.Artist.EventArtist.EventArtist;
type SearchResultArtist = API.Artist.SearchResultArtist.SearchResultArtist;

interface Props {
  artists?: {
    listId: number;
    formId: string;
    artist: AnnounceArtist;
  }[];
  newArtists?: EventArtist[];
  eventId?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'add'): void;
  (e: 'edit', artistId: number): void;
  (e: 'remove', listId: number): void;
}>();

const { $gettext } = useGettext();

const accountStore = useAccountStore();
const eventArtistStore = useEventArtistStore();
const toast = useToast();

const newArtistsEnabled = computed(() => accountStore.features.indexOf('new_event_artists') !== -1);
const allArtists = ref<EventArtist[]>([]);

const add = () => emit('add');
const remove = (listId: number) => emit('remove', listId);

watch(() => props.newArtists || [], (newValue: EventArtist[]) => {
  allArtists.value = [...newValue];
}, { immediate: true });

const onArtistAdd = (artist: SearchResultArtist) => {
  addArtist(artist);
};

const onArtistSelect = (artist: SearchResultArtist) => {
  addArtist(artist);
};

const addArtist = async (artist: SearchResultArtist) => {
  try {
    if (props.eventId) {
      const newArtist = await eventArtistStore.addEventArtist(props.eventId, artist);
      allArtists.value.push(newArtist);
      toast.success($gettext('Artist added successfully'));
    }
  } catch (e) {
    toast.error($gettext('Failed to add artist'));
    Sentry.captureException(e);
  }
};

const onEventArtistRemove = async (eventArtist: EventArtist) => {
  if (!confirm($gettext('Are you sure you want to remove the artist %{name}?', { name: eventArtist.artist?.name }))) {
    return;
  }

  try {
    const idx = allArtists.value.indexOf(eventArtist);
    await eventArtistStore.removeEventArtist(eventArtist.id);
    allArtists.value.splice(idx, 1);
    toast.success($gettext('Artist removed successfully'));
  } catch (e) {
    toast.error($gettext('Failed to remove artist'));
    Sentry.captureException(e);
  }
};

const artistsReordering = computed(() => eventArtistStore.eventArtistsReordering[props.eventId!] === LoadingState.LOADING);

const onArtistsReorder = async (artists: { id: number }[]) => {
  if (!props.eventId) {
    return;
  }

  const original = allArtists.value;
  allArtists.value = artists;

  try {
    allArtists.value = await eventArtistStore.reorderEventArtists(props.eventId, artists.map(({ id }) => id));
    toast.success($gettext('Artists reordered successfully'));
  } catch (e) {
    allArtists.value = original;
    toast.error($gettext('Failed to reorder artists'));
    Sentry.captureException(e);
  }
};
</script>
